import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableContainer,
  TableCell,
  Paper,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import DateConverter from "../../utils/DateConverter";
import { BlobProvider } from "@react-pdf/renderer";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LoopIcon from "@mui/icons-material/Loop";
import ClientInfoModal from "../client/ClientInfoModal";
import {
  PrimaryColor,
  TableBodyGrey,
  TableHeaderGrey,
  TableRowHover,
} from "../../shared/styles/colors";
import { DatabaseEntities } from "../../model/enum/database-entities.enum";
import { Card, CardContent, Typography, Button } from "@material-ui/core";
import { TablePagination } from "@mui/material";
import { Spinner } from "../../components";
import { ToastContainer } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import ApiService from "../../service/ApiService";
import Estimate from "../../model/estimate";
import EstimateModal from "./EstimateModal";
import DB_Status from "../../model/enum/status.class";
import CachedIcon from "@mui/icons-material/Cached";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EstimateDetailModal from "./EstimateDetailModal";
import EstimatePreview from "./EstimatePreview";
import Pathnames from "../../model/enum/Pathnames";
import PageResultResponse from "../../model/page-result-response";
import useStyles from "../styles/cardStyles";
import Misc from "../../utils/Misc";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: TableHeaderGrey,
    color: PrimaryColor,
    fontWeight: 700,
    whiteSpace: "nowrap",
    zIndex: 0,
    position: "static",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: TableBodyGrey,
    fontFamily: "monospace",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: TableRowHover,
  },
}));

const Estimates = () => {
  const classes = useStyles();
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    url.searchParams.set("rowsPerPage", rowsPerPage.toString());
    window.history.pushState({}, Pathnames.ESTIMATES_LIST, url);
  });

  // Get all the estimates from the database
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      searchParams.set("page", page.toString());
      searchParams.set("rowsPerPage", rowsPerPage.toString());

      await ApiService.get<PageResultResponse>(
        `${DatabaseEntities.ESTIMATES}?${searchParams.toString()}`
      )
        .then((data) => {
          if (Misc.isArrayEmpty(data.items)) return;
          const { items, count } = data;
          setEstimates(items);
          setTotalPages(count);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    if (loading && Misc.isArrayEmpty(estimates)) fetchData();
  }, [estimates]);

  const resetList = () => {
    setPage(1);
    setRowsPerPage(10);
    setEstimates([]);
    setLoading(true);
    if (searchParams.has("estimateCode")) {
      searchParams.delete("estimateCode");
      setSearchParams(searchParams);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown> | null,
    pg: number
  ) => {
    setPage(pg + 1);
    scrollToTop();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let row = parseInt(event.target.value);
    setRowsPerPage(row);
    scrollToTop();
  };

  const scrollToTop = () => {
    setEstimates([]);
    setLoading(true);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const addIdToQueryPath = (code?: string) => {
    if (!code) return;

    const url = new URL(window.location.href);
    url.searchParams.set("estimateCode", code);
    window.history.pushState({}, Pathnames.APPOINTMENTS_LIST, url);
  };

  const removeIdFromQueryPath = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("estimateCode");
    window.history.replaceState({}, "", url.pathname + url.search);
  };

  return (
    <>
      <Card raised className={classes.card}>
        <div className={classes.headerContainer}>
          <Typography className={classes.title}>Estimates</Typography>
          <div className={classes.headerDiv}>
            <EstimateModal resetList={resetList} />
            <Button onClick={resetList}>
              <CachedIcon />
            </Button>
          </div>
        </div>
        <CardContent>
          <Paper>
            <TableContainer>
              <Table
                stickyHeader
                aria-label="customized table"
                padding="normal"
                size="medium"
              >
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="center">
                      <MoreVertIcon />
                    </StyledTableCell>
                    <StyledTableCell align="right">Client</StyledTableCell>
                    <StyledTableCell align="right">Code</StyledTableCell>
                    <StyledTableCell align="right">Total Value</StyledTableCell>
                    <StyledTableCell align="right">
                      Starting Date
                    </StyledTableCell>
                    <StyledTableCell align="right">Due Date</StyledTableCell>
                    <StyledTableCell align="right">Status</StyledTableCell>
                    <StyledTableCell align="right">Created At</StyledTableCell>
                    <StyledTableCell align="right">PDF</StyledTableCell>
                  </TableRow>
                </TableHead>
                {loading ? (
                  <Spinner />
                ) : (
                  <TableBody>
                    {estimates.length > 0 ? (
                      estimates.map((row: Estimate) => (
                        <StyledTableRow key={row.code}>
                          <StyledTableCell align="right">
                            <EstimateDetailModal
                              estimate={row}
                              resetList={resetList}
                              onOpen={() => {
                                addIdToQueryPath(row?.code);
                              }}
                              onClose={() => {
                                removeIdFromQueryPath();
                              }}
                            />
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <ClientInfoModal clientData={row.Client} />
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            #{row.code}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {Misc.toUSD(row.total)}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {DateConverter.toFullDate(row.startsAt)}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {DateConverter.toFullDate(row.dueDate)}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <Typography
                              style={DB_Status.mapStatusColor(row.Status?.name)}
                            >
                              <div
                                style={DB_Status.mapStatusRoundedColor(
                                  row.Status?.name
                                )}
                              ></div>
                              {row.Status?.name}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {DateConverter.toFullDate(row.createdAt)}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <BlobProvider
                              document={<EstimatePreview estimate={row} />}
                            >
                              {({ url }: any) => {
                                const filename = `${
                                  row.code
                                }_${DateConverter.toFullDate(
                                  row.createdAt
                                )}.pdf`;
                                return (
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    //download={filename}
                                  >
                                    <Button>
                                      {url ? (
                                        <PictureAsPdfIcon />
                                      ) : (
                                        <LoopIcon />
                                      )}
                                    </Button>
                                  </a>
                                );
                              }}
                            </BlobProvider>
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    ) : (
                      <StyledTableCell align="right">No items</StyledTableCell>
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Paper>
          <TablePagination
            component="div"
            count={totalPages}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
      <ToastContainer />
    </>
  );
};

export default Estimates;
